
import * as React from "react"
import * as ToastPrimitives from "@radix-ui/react-toast"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../lib/utils"

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => {
  React.useEffect(() => {
    const killPointerDownInToast = (e: Event) => {
      const target = e.target as Element | null;
      if (target?.closest?.("[data-toast-viewport]")) {
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener("pointerdown", killPointerDownInToast, true);
    return () => {
      document.removeEventListener("pointerdown", killPointerDownInToast, true);
    };
  }, []);

  return (
    <ToastPrimitives.Viewport
      ref={ref}
      // Marker attribute consumed by DialogContent (and any other Radix
      // DismissableLayer-based component) to ignore outside-interaction
      // events that originate inside a toast — so clicking the toast's
      // close X doesn't also close the dialog underneath.
      data-toast-viewport=""
      className={cn(
        "fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:bottom-0 sm:right-0 sm:top-auto sm:flex-col md:max-w-[420px]",
        className
      )}
      {...props}
    />
  );
})
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-center justify-between space-x-4 overflow-hidden rounded-md border p-6 pr-8 shadow-lg transition-all data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
  {
    variants: {
      variant: {
        default: "border bg-background text-foreground",
        destructive:
          "destructive group border-destructive bg-destructive text-destructive-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const ToastRoot = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root>
>((props, ref) => {
  const [selecting, setSelecting] = React.useState(false);

  // Stop pointer-down events from bubbling out of the toast. Radix
  // DismissableLayer (used by Dialog / Popover / Sheet) attaches its
  // outside-click listener to the document, so without this, clicking
  // the toast — including its close X — registers as "outside" any
  // open dialog underneath and dismisses it. stopImmediatePropagation
  // on the native event prevents the document listener from firing.
  const stopOutsidePropagation = (
    e: React.PointerEvent<HTMLLIElement> | React.MouseEvent<HTMLLIElement>,
  ) => {
    e.stopPropagation();
    (e.nativeEvent as Event).stopImmediatePropagation();
  };

  return (
    <ToastPrimitives.Root
      duration={5000}
      ref={ref}
      {...props}
      onPointerDown={(e) => {
        stopOutsidePropagation(e);
        props.onPointerDown?.(e);
      }}
      onPointerDownCapture={(e) => {
        e.stopPropagation();
        props.onPointerDownCapture?.(e);
      }}
      onClick={(e) => {
        stopOutsidePropagation(e);
        props.onClick?.(e);
      }}
      onMouseDown={(e) => {
        setSelecting(true);
        props.onMouseDown?.(e);
      }}
      onMouseUp={(e) => {
        if (selecting && window.getSelection()?.toString()) {
          e.stopPropagation();
          e.preventDefault();
        }
        setSelecting(false);
        props.onMouseUp?.(e);
      }}
    />
  );
});
ToastRoot.displayName = ToastPrimitives.Root.displayName;

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastRoot>,
  React.ComponentPropsWithoutRef<typeof ToastRoot> &
    VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
  return (
    <ToastRoot
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    />
  )
})
Toast.displayName = 'Toast'

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className
    )}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, onPointerDown, onMouseDown, onClick, ...props }, ref) => {
  const stopAll = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    (e.nativeEvent as Event).stopImmediatePropagation();
  };
  return (
    <ToastPrimitives.Close
      ref={ref}
      className={cn(
        "absolute right-2 top-2 rounded-md p-1 text-foreground/70 opacity-100 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-2 group-hover:opacity-100 cursor-pointer pointer-events-auto group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:focus:ring-red-400 group-[.destructive]:focus:ring-offset-red-600",
        className
      )}
      toast-close=""
      onPointerDown={(e) => {
        stopAll(e);
        onPointerDown?.(e);
      }}
      onMouseDown={(e) => {
        stopAll(e);
        onMouseDown?.(e);
      }}
      onClick={(e) => {
        stopAll(e);
        onClick?.(e);
      }}
      {...props}
    >
      <X className="h-4 w-4" />
    </ToastPrimitives.Close>
  );
})
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn("text-sm font-semibold", className)}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm opacity-90", className)}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
}

